import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { TolgeeProvider } from '@tolgee/react';
import * as Sentry from '@sentry/react';
import { tolgee } from './i18n';
import './index.css';
import App from './App';
import { getAppEnvironment } from './utils/environment';

// Global declaration for TypeScript compiler
declare const __APP_VERSION__: string;

// Set application version for DevTools and console
(window as any).APP_VERSION = __APP_VERSION__;
console.log(`%cCentrum Coacha i Terapeuty ${__APP_VERSION__} [Env: ${getAppEnvironment()}]`, 'color: #8b5cf6; font-weight: bold;');

// Fetch DSN from Vite environment variables
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

if (sentryDsn) {
  // Initialize Sentry with PII protection for client confidentiality
  Sentry.init({
    dsn: sentryDsn,
    release: __APP_VERSION__,
    // Label environment (e.g. production-web, staging-desktop)
    environment: `${import.meta.env.MODE}-${getAppEnvironment()}`,
    
    // Integrations: Tracing (metrics and performance)
    integrations: [
      Sentry.browserTracingIntegration(),
    ],

    // Performance tracking and metrics
    tracesSampleRate: 1.0,

    // Pre-send scrubber to remove sensitive PII
    beforeSend(event) {
      // 1. Remove user PII (do not log emails, usernames, or IPs)
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
        delete event.user.ip_address;
      }

      const redactPII = (text: string): string => {
        if (!text) return text;

        // Email regex
        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
        // Stripe Secret/Publishable Keys regex
        const stripeKeyRegex = /(sk|pk)_(live|test)_[a-zA-Z0-9]+/g;

        let scrubbed = text
          .replace(emailRegex, '[EMAIL]')
          .replace(stripeKeyRegex, '[STRIPE_KEY]');

        // Remove sensitive Realtime Database paths (e.g., client notes, sessions)
        scrubbed = scrubbed.replace(/\/users\/[^/]+\/notes\/[^/]+/g, '/users/[USER_ID]/notes/[NOTE_ID]');
        scrubbed = scrubbed.replace(/\/users\/[^/]+\/coachingSessions/g, '/users/[USER_ID]/coachingSessions');
        scrubbed = scrubbed.replace(/\/users\/[^/]+\/stripeCustomerId/g, '/users/[USER_ID]/stripeCustomerId');

        return scrubbed;
      };

      // 2. Scrub exception messages and descriptions
      if (event.exception?.values) {
        event.exception.values.forEach((value) => {
          if (value.value) value.value = redactPII(value.value);
          if (value.type) value.type = redactPII(value.type);
        });
      }

      // 3. Scrub breadcrumbs (console logs, click events, and routing)
      if (event.breadcrumbs) {
        event.breadcrumbs.forEach((breadcrumb) => {
          if (breadcrumb.message) {
            breadcrumb.message = redactPII(breadcrumb.message);
          }
          if (breadcrumb.data) {
            const dataStr = JSON.stringify(breadcrumb.data);
            if (dataStr) {
              try {
                breadcrumb.data = JSON.parse(redactPII(dataStr));
              } catch (e) {
                breadcrumb.data = {};
              }
            }
          }
        }
        );
      }

      // 4. Scrub extra context
      if (event.extra) {
        const extraStr = JSON.stringify(event.extra);
        if (extraStr) {
          try {
            event.extra = JSON.parse(redactPII(extraStr));
          } catch (e) {
            event.extra = {};
          }
        }
      }

      return event;
    }
  });
}



const rootElement = document.getElementById('root');


if (!rootElement) {
  throw new Error('Failed to find the root element');
}

createRoot(rootElement).render(
  <StrictMode>
    <TolgeeProvider tolgee={tolgee} fallback="Ładowanie...">
      <App />
    </TolgeeProvider>
  </StrictMode>
);


